import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Calendar, ArrowRight } from 'lucide-react';
import { newsInsights } from '../mock';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';

const Insights = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setSuccessMessage('');

    try {
      const { data, error } = await supabase
        .from('contact_submissions')
        .insert([
          {
            name: 'VALENTIS SUBSCRIBER',
            email: email.trim(),
            message: 'Newsletter subscription',
            service_interest: 'Newsletter',
            source: 'website_newsletter'
          }
        ]);

      if (error) throw error;

      toast.success('Successfully subscribed to our newsletter!');
      setSuccessMessage('MESSAGE STORED');
      setEmail('');
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-cream text-ink">
      <Helmet>
        <title>Insights & Legal Updates | Valentis Law Group</title>
        <meta name="description" content="Read the latest legal insights, updates, and analysis from Valentis Law Group on cross-border disputes, arbitration, and international enforcement." />
        <link rel="canonical" href="https://valentislawgroup.com/insights" />
        <meta property="og:title" content="Insights & Legal Updates | Valentis Law Group" />
        <meta property="og:description" content="Read the latest legal insights, updates, and analysis from Valentis Law Group on cross-border disputes, arbitration, and international enforcement." />
        <meta property="og:url" content="https://valentislawgroup.com/insights" />
        <meta name="twitter:title" content="Insights & Legal Updates | Valentis Law Group" />
        <meta name="twitter:description" content="Read the latest legal insights, updates, and analysis from Valentis Law Group on cross-border disputes, arbitration, and international enforcement." />
      </Helmet>
      {/* Hero Section */}
      <section className="pt-40 pb-24 lg:pt-48 lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-4xl">
            <p className="text-sm uppercase tracking-widest text-ink/60 mb-6">News & Insights</p>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-wide leading-tight mb-8">
              Legal Intelligence & Thought Leadership
            </h1>
            <p className="text-xl text-ink/70 leading-relaxed">
              Stay informed with the latest legal developments, regulatory updates, and strategic insights from our team of expert attorneys.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Insight */}
      <section className="py-24 lg:py-32 border-t border-ink/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <div className="inline-block px-4 py-2 border border-ink/20 text-xs tracking-widest uppercase">
                Featured Insight
              </div>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-wide leading-tight">
                {newsInsights[0].title}
              </h2>
              <div className="flex items-center space-x-4 text-sm text-ink/60">
                <span className="flex items-center">
                  <Calendar size={16} className="mr-2" />
                  {newsInsights[0].date}
                </span>
                <span className="text-ink/40">|</span>
                <span>{newsInsights[0].category}</span>
              </div>
              <p className="text-lg text-ink/70 leading-relaxed">
                {newsInsights[0].excerpt}
              </p>
              <Button
                asChild
                className="bg-ink text-cream hover:bg-ink/90 font-medium tracking-wider transition-all duration-300"
              >
                <Link to={`/article/${newsInsights[0].slug}`}>
                  Read Full Article
                  <ArrowRight className="ml-2" size={18} />
                </Link>
              </Button>
            </div>

            <div className="bg-gradient-to-br from-ink/10 to-ink/5 border border-ink/10 h-96 flex items-center justify-center">
              <div className="text-center p-8">
                <p className="text-6xl font-serif mb-4">01</p>
                <p className="text-sm tracking-widest uppercase text-ink/60">Featured Article</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Recent Insights */}
      <section className="py-24 lg:py-32 bg-sand-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-16">
            <h2 className="font-serif text-4xl md:text-5xl tracking-wide mb-6">Recent Publications</h2>
            <p className="text-lg text-ink/70">Expert analysis and commentary on critical legal developments</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {newsInsights.slice(1).map((insight) => (
              <div
                key={insight.id}
                className="group bg-cream border border-ink/10 p-10 hover:border-ink/30 transition-all duration-500"
              >
                <div className="inline-block px-3 py-1 border border-ink/20 text-xs tracking-widest uppercase mb-6">
                  {insight.category}
                </div>
                <h3 className="font-serif text-3xl mb-4 group-hover:text-ink/90 transition-colors duration-300">
                  {insight.title}
                </h3>
                <div className="flex items-center space-x-4 text-sm text-ink/60 mb-6">
                  <span className="flex items-center">
                    <Calendar size={16} className="mr-2" />
                    {insight.date}
                  </span>
                </div>
                <p className="text-ink/60 leading-relaxed mb-6">
                  {insight.excerpt}
                </p>
                <Link 
                  to={`/article/${insight.slug}`}
                  className="inline-flex items-center text-sm tracking-wider text-ink/80 hover:text-ink transition-colors duration-300"
                >
                  Read More
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" size={16} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      {/* <section className="py-24 lg:py-32 border-t border-ink/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl tracking-wide mb-6">Explore by Practice Area</h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {['Corporate Law', 'Litigation', 'M&A', 'Intellectual Property', 'Banking & Finance', 'Tax Law', 'Employment', 'Regulatory'].map((category, index) => (
              <button
                key={index}
                className="bg-ink/5 backdrop-blur-sm border border-ink/10 p-6 hover:bg-ink/10 hover:border-ink/30 transition-all duration-300 text-center"
              >
                <p className="text-sm tracking-wider">{category}</p>
              </button>
            ))}
          </div>
        </div>
      </section> */}

      {/* Newsletter Signup */}
      <section className="py-24 lg:py-32 bg-sand-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-serif text-4xl md:text-5xl tracking-wide mb-8">
            Stay Informed
          </h2>
          <p className="text-xl text-ink/70 mb-12">
            Subscribe to receive our latest insights, legal updates, and thought leadership directly in your inbox.
          </p>
          <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email address"
              className="flex-1 px-6 py-4 bg-ink/5 border border-ink/10 text-ink placeholder:text-ink/40 focus:outline-none focus:border-ink/30 transition-colors duration-300"
              disabled={isSubmitting}
              required
            />
            <Button 
              type="submit"
              className="bg-ink text-cream hover:bg-ink/90 px-8 py-4 font-medium tracking-wider whitespace-nowrap"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Subscribing...' : 'Subscribe'}
            </Button>
          </form>
          {successMessage && (
            <div className="mt-6 text-center">
              <p className="text-green-500 font-semibold text-lg tracking-wider">
                {successMessage}
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Insights;